import { dlog } from './debug';
/**
 * COLOR CLUSTERING SYSTEM
 * 
 * Handles user-generated content (UGC) colors by grouping similar colors
 * into consistent emotion palettes. Prevents color averaging issues where
 * slightly different pinks might average to orange.
 */

// Color family definitions with tolerance ranges
export const COLOR_FAMILIES = {
  red: {
    name: 'Red',
    baseColor: '#FF0000',
    tolerance: 30, // HSL hue tolerance
    emotions: ['competitive', 'resilient']
  },
  pink: {
    name: 'Pink', 
    baseColor: '#FF69B4',
    tolerance: 80,
    emotions: ['joyful', 'calm']
  },
  orange: {
    name: 'Orange',
    baseColor: '#FFA500', 
    tolerance: 25,
    emotions: ['excited', 'determined']
  },
  yellow: {
    name: 'Yellow',
    baseColor: '#FFFF00',
    tolerance: 30,
    emotions: ['joyful', 'proud']
  },
  green: {
    name: 'Green',
    baseColor: '#00FF00',
    tolerance: 35,
    emotions: ['calm', 'focused']
  },
  teal: {
    name: 'Teal',
    baseColor: '#008080',
    tolerance: 25,
    emotions: ['focused', 'reflective']
  },
  blue: {
    name: 'Blue',
    baseColor: '#0000FF',
    tolerance: 30,
    emotions: ['calm', 'reflective']
  },
  purple: {
    name: 'Purple',
    baseColor: '#800080',
    tolerance: 25,
    emotions: ['reflective', 'resilient']
  },
  brown: {
    name: 'Brown',
    baseColor: '#A52A2A',
    tolerance: 20,
    emotions: ['calm', 'focused']
  },
  gray: {
    name: 'Gray',
    baseColor: '#808080',
    tolerance: 15,
    emotions: ['focused', 'reflective']
  }
};

/**
 * Convert hex color to HSL
 * @param {string} hex - Hex color string (#RRGGBB)
 * @returns {object} HSL object with h, s, l properties
 */
export function hexToHsl(hex) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
export function hslToHex(h, s, l) {
  h = h / 360;
  s = s / 100;
  l = l / 100;
  
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return `#${Math.round(r * 255).toString(16).padStart(2, '0')}${Math.round(g * 255).toString(16).padStart(2, '2', '0')}${Math.round(b * 255).toString(16).padStart(2, '0')}`;
}

/**
 * Calculate distance between two HSL colors
 * @param {object} color1 - HSL object
 * @param {object} color2 - HSL object
 * @returns {number} Distance value (lower = more similar)
 */
export function getHslDistance(color1, color2) {
  // Handle hue wraparound (0 and 360 are the same)
  let hDiff = Math.abs(color1.h - color2.h);
  if (hDiff > 180) {
    hDiff = 360 - hDiff;
  }
  
  // Weighted distance calculation
  // Hue is most important for color family, saturation and lightness for variations
  const hueWeight = 2.0;
  const satWeight = 0.5;
  const lightWeight = 0.5;
  
  return Math.sqrt(
    Math.pow(hDiff * hueWeight, 2) +
    Math.pow((color1.s - color2.s) * satWeight, 2) +
    Math.pow((color1.l - color2.l) * lightWeight, 2)
  );
}

/**
 * Find the closest color family for a given color
 * @param {string} hexColor - Hex color string
 * @returns {object} Color family object with match confidence
 */
export function findColorFamily(hexColor) {
  const colorHsl = hexToHsl(hexColor);
  let bestMatch = null;
  let bestDistance = Infinity;
  
  // Check each color family
  for (const [familyName, family] of Object.entries(COLOR_FAMILIES)) {
    const familyHsl = hexToHsl(family.baseColor);
    const distance = getHslDistance(colorHsl, familyHsl);
    
    // Check if within tolerance
    if (distance <= family.tolerance && distance < bestDistance) {
      bestMatch = {
        name: familyName,
        family: family,
        distance: distance,
        confidence: Math.max(0, 1 - (distance / family.tolerance))
      };
      bestDistance = distance;
    }
  }
  
  // If no match found, return a default
  if (!bestMatch) {
    return {
      name: 'gray',
      family: COLOR_FAMILIES.gray,
      distance: 100,
      confidence: 0.1
    };
  }
  
  return bestMatch;
}

/**
 * Get the representative color for a color family
 * @param {string} familyName - Name of the color family
 * @param {string} originalColor - Original hex color for reference
 * @returns {string} Representative hex color
 */
export function getFamilyRepresentativeColor(familyName, originalColor = null) {
  const family = COLOR_FAMILIES[familyName];
  if (!family) {
    return COLOR_FAMILIES.gray.baseColor;
  }
  
  // If we have an original color, blend it slightly with the base color
  // to maintain some of the user's specific choice
  if (originalColor) {
    const originalHsl = hexToHsl(originalColor);
    const baseHsl = hexToHsl(family.baseColor);
    
    // Blend 70% base color, 30% original color
    const blendedHsl = {
      h: Math.round(baseHsl.h * 0.7 + originalHsl.h * 0.3),
      s: Math.round(baseHsl.s * 0.7 + originalHsl.s * 0.3),
      l: Math.round(baseHsl.l * 0.7 + originalHsl.l * 0.3)
    };
    
    return hslToHex(blendedHsl.h, blendedHsl.s, blendedHsl.l);
  }
  
  return family.baseColor;
}

/**
 * Get emotions associated with a color family
 * @param {string} familyName - Name of the color family
 * @returns {array} Array of emotion names
 */
export function getFamilyEmotions(familyName) {
  const family = COLOR_FAMILIES[familyName];
  return family ? family.emotions : ['focused'];
}

/**
 * Process a list of user colors and return clustered results
 * @param {array} userColors - Array of hex color strings
 * @returns {object} Clustered color data with families and counts
 */
export function clusterUserColors(userColors) {
  const clusters = {};
  const colorCounts = {};
  
  // Count occurrences of each color
  userColors.forEach(color => {
    colorCounts[color] = (colorCounts[color] || 0) + 1;
  });
  
  // Group colors by family
  Object.keys(colorCounts).forEach(color => {
    const familyMatch = findColorFamily(color);
    const familyName = familyMatch.name;
    
    if (!clusters[familyName]) {
      clusters[familyName] = {
        family: COLOR_FAMILIES[familyName],
        colors: [],
        totalCount: 0,
        representativeColor: getFamilyRepresentativeColor(familyName, color)
      };
    }
    
    clusters[familyName].colors.push({
      color: color,
      count: colorCounts[color],
      confidence: familyMatch.confidence
    });
    
    clusters[familyName].totalCount += colorCounts[color];
  });
  
  return clusters;
}

/**
 * Get the most used color family from clustered data
 * @param {object} clusters - Clustered color data
 * @returns {object} Most used color family info
 */
export function getMostUsedColorFamily(clusters) {
  // Find the maximum count
  let maxCount = 0;
  Object.values(clusters).forEach(cluster => {
    if (cluster.totalCount > maxCount) {
      maxCount = cluster.totalCount;
    }
  });
  
  // Get all families with the maximum count
  const tiedFamilies = Object.entries(clusters).filter(([_, cluster]) => cluster.totalCount === maxCount);
  
  // If there's a tie, randomly pick one (but prefer colorful over gray)
  let selectedFamily;
  if (tiedFamilies.length === 1) {
    selectedFamily = tiedFamilies[0];
  } else {
    // Prefer colorful families over gray in ties
    const colorfulFamilies = tiedFamilies.filter(([name, _]) => name !== 'gray');
    if (colorfulFamilies.length > 0) {
      // Randomly pick from colorful families
      selectedFamily = colorfulFamilies[Math.floor(Math.random() * colorfulFamilies.length)];
    } else {
      // If all are gray, pick randomly
      selectedFamily = tiedFamilies[Math.floor(Math.random() * tiedFamilies.length)];
    }
  }
  
  const [familyName, cluster] = selectedFamily;
  return {
    name: familyName,
    family: cluster.family,
    count: cluster.totalCount,
    representativeColor: cluster.representativeColor,
    emotions: getFamilyEmotions(familyName)
  };
}
